import React from 'react';
import { Eye, ListChecks, Maximize2, Plus, ShieldCheck, Sparkles, Undo2, X } from 'lucide-react';
import { useAgent } from '../AgentProvider';
import { humanize, promptsForTab } from '../agentContextMap';
import { useConversationController } from '../useConversationController';
import Composer from './Composer';
import Conversation from './Conversation';
import ProfilePicker from './ProfilePicker';

export function ContextChip({ context }) {
  if (!context) return null;
  const parts = [context.tabLabel || humanize(context.tab || '')];
  if (context.entity) parts.push(`${humanize(context.entity.type)} ${context.entity.label || context.entity.id}`);
  return (
    <div className="dagent-context" title="The agent receives this screen context with your message">
      <Eye size={13} aria-hidden="true" />
      <span>Sees: {parts.filter(Boolean).join(' · ')}</span>
    </div>
  );
}

export function EmptyState({ title = 'What should we get done?' }) {
  return (
    <div className="dagent-empty">
      <span className="dagent-empty__mark">
        <Sparkles size={20} aria-hidden="true" />
      </span>
      <h3>{title}</h3>
      <p>Ask for desk work in plain words: check-outs, balances, reminders, stock, reports.</p>
      <ul>
        <li>
          <ListChecks size={14} aria-hidden="true" /> Batches of changes wait for one approval
        </li>
        <li>
          <Undo2 size={14} aria-hidden="true" /> Executed actions can be undone
        </li>
        <li>
          <ShieldCheck size={14} aria-hidden="true" /> Every step is kept in a tamper-proof log
        </li>
      </ul>
    </div>
  );
}

function DrawerBody({ onOpenWorkspace }) {
  const agent = useAgent();
  const controller = useConversationController({
    sessionId: agent.drawerSessionId,
    setSessionId: agent.setDrawerSessionId,
    originTab: agent.pageContext.tab,
  });
  const isEmpty = controller.agentSession.events.length === 0;

  return (
    <>
      <header className="dagent-drawer__head">
        <div className="dagent-brand">
          <span className="dagent-brand__mark">
            <Sparkles size={16} aria-hidden="true" />
          </span>
          <div>
            <div className="dagent-kicker">Operations agent</div>
            <div className="dagent-brand__title">Dridh Agent</div>
          </div>
        </div>
        <div className="dagent-drawer__tools">
          <button type="button" className="dagent-icon-btn" title="New conversation" onClick={() => agent.setDrawerSessionId(null)}>
            <Plus size={16} aria-hidden="true" />
          </button>
          {onOpenWorkspace && (
            <button
              type="button"
              className="dagent-icon-btn"
              title="Open in the Agent tab"
              onClick={() => {
                agent.setOpen(false);
                onOpenWorkspace(agent.drawerSessionId);
              }}
            >
              <Maximize2 size={15} aria-hidden="true" />
            </button>
          )}
          <button type="button" className="dagent-icon-btn" title="Close (Esc)" onClick={() => agent.setOpen(false)}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </header>
      <div className="dagent-drawer__meta">
        <ContextChip context={agent.pageContext} />
        <ProfilePicker onConfigure={onOpenWorkspace ? () => onOpenWorkspace(null, 'settings') : undefined} />
      </div>
      <Conversation controller={controller} emptyState={<EmptyState />} />
      <Composer
        disabled={!agent.meta?.profiles?.length || controller.readOnly}
        running={controller.running}
        onSend={controller.send}
        onStop={controller.stop}
        suggestions={isEmpty ? promptsForTab(agent.pageContext.tab) : []}
        draft={agent.draft}
        clearDraft={() => agent.setDraft('')}
        placeholder={controller.awaiting ? 'Approve above, or send a new message to skip' : undefined}
      />
    </>
  );
}

function AgentDrawer({ onOpenWorkspace }) {
  const agent = useAgent();
  if (!agent?.enabled) return null;

  return (
    <>
      {agent.open && <div className="dagent-scrim" onClick={() => agent.setOpen(false)} aria-hidden="true" />}
      <aside className={`dagent-drawer ${agent.open ? 'is-open' : ''}`} aria-hidden={!agent.open} aria-label="Dridh Agent">
        {agent.open && <DrawerBody onOpenWorkspace={onOpenWorkspace} />}
      </aside>
    </>
  );
}

export default AgentDrawer;
